import { useEffect, useState } from 'react';
import { loadPerformance } from '../../lib/performanceService';
import AccessDenied from './AccessDenied';
import {
  networkTotals,
  euros,
  percent,
  type NetworkPerformance,
  type TenantPerf,
} from '../../lib/performance';

/**
 * Section « Performance métier » du SuperAdmin (brique E) : comparatif réseau sur une
 * fenêtre glissante — commandes, CA, ticket moyen, % paiement en ligne, par resto + réseau.
 */
const WINDOWS = [7, 30, 90];

export default function PerformanceSection() {
  const [days, setDays] = useState(30);
  const [perf, setPerf] = useState<NetworkPerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    loadPerformance(days).then((res) => {
      if (res.ok) setPerf(res.data);
      else setError(res.error ?? 'Chargement impossible.');
      setLoading(false);
    });
  }, [days]);

  if (error) {
    return <AccessDenied title="Performance métier" sqlFile="sql/26_performance.sql" />;
  }

  const tenants: TenantPerf[] = perf?.tenants ?? [];
  const net = networkTotals(tenants);

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h1 className="text-xl font-bold">Performance métier réseau</h1>
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setDays(w)}
              className={'text-sm rounded-full px-3 py-1 border ' + (w === days ? 'text-white border-transparent' : 'opacity-70')}
              style={w === days ? { background: '#2563EB' } : {}}
            >
              {w} j
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="opacity-70">Calcul de la performance…</p>
      ) : (
        <>
          {/* KPIs réseau */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <Kpi label="Commandes" value={String(net.orders)} />
            <Kpi label="CA réseau" value={euros(net.revenueCents)} />
            <Kpi label="Ticket moyen" value={euros(net.avgTicketCents)} />
            <Kpi label="Paiement en ligne" value={percent(net.onlineRatio)} />
          </div>

          {/* Par resto */}
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase opacity-60">
                <tr>
                  <th className="px-3 py-2">Restaurant</th>
                  <th className="px-3 py-2 text-right">Cmd</th>
                  <th className="px-3 py-2 text-right">CA</th>
                  <th className="px-3 py-2 text-right">Ticket moyen</th>
                  <th className="px-3 py-2 text-right">% en ligne</th>
                  <th className="px-3 py-2 text-right">Ticket vs réseau</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => {
                  const delta = net.avgTicketCents > 0 ? t.avgTicketCents - net.avgTicketCents : 0;
                  return (
                    <tr key={t.tenantId} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium">
                        {t.name} <span className="opacity-50 text-xs">/{t.slug}</span>
                      </td>
                      <td className="px-3 py-2 text-right">{t.orders}</td>
                      <td className="px-3 py-2 text-right">{euros(t.revenueCents)}</td>
                      <td className="px-3 py-2 text-right">{euros(t.avgTicketCents)}</td>
                      <td className="px-3 py-2 text-right">{percent(t.onlineRatio)}</td>
                      <td
                        className="px-3 py-2 text-right font-medium"
                        style={{ color: delta > 0 ? '#16A34A' : delta < 0 ? '#DC2626' : '#64748B' }}
                      >
                        {t.orders === 0 ? '—' : (delta >= 0 ? '+' : '') + euros(delta)}
                      </td>
                    </tr>
                  );
                })}
                {tenants.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center opacity-50">
                      Aucune donnée sur la période.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-3">
      <div className="text-xs uppercase opacity-50">{label}</div>
      <div className="text-xl font-extrabold">{value}</div>
    </div>
  );
}
